import React, { useEffect, useMemo, useRef } from 'react';
import { Card, Col, Progress, Row, Tag, Typography } from 'antd';
import {
  ApartmentOutlined,
  BranchesOutlined,
  FireOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import {
  AirflowAnalysisResult,
  AirflowHeatmapCell,
  AirflowPath,
  ImageAnalysisResult,
} from '../types';

const { Paragraph, Title } = Typography;

interface AirflowVisualizationProps {
  imageAnalysis: ImageAnalysisResult;
  airflowAnalysis: AirflowAnalysisResult;
  previewUrl: string;
}

const levelLabels: Record<AirflowHeatmapCell['level'], string> = {
  low: '偏弱',
  balanced: '均衡',
  strong: '偏强',
};

const levelTagColors: Record<AirflowHeatmapCell['level'], string> = {
  low: 'blue',
  balanced: 'green',
  strong: 'red',
};

const formatPercent = (value: number) => Math.round(value * 100);
const MAX_ANIMATED_PATHS = 4;
const MAX_PATH_PARTICLES = 120;
const SOURCE_PARTICLE_COUNT = 18;

type Point = { x: number; y: number };
type PathSegment = {
  from: Point;
  to: Point;
  length: number;
  start: number;
  vector: Point;
  normal: Point;
};
type PathMetric = {
  path: AirflowPath;
  index: number;
  length: number;
  segments: PathSegment[];
};
type CanvasParticle = {
  id: string;
  kind: 'source' | 'path';
  pathIndex?: number;
  x: number;
  y: number;
  driftX: number;
  driftY: number;
  offset: number;
  radius: number;
  opacity: number;
  speed: number;
  delay: number;
};

const hash01 = (value: number) => {
  const raw = Math.sin(value * 12.9898) * 43758.5453;
  return raw - Math.floor(raw);
};

const airflowCloudColor = (value: number) => {
  if (value < 0.18) return 'rgba(37, 99, 235, 0.2)';
  if (value < 0.38) return 'rgba(14, 165, 233, 0.24)';
  if (value < 0.58) return 'rgba(34, 197, 94, 0.22)';
  if (value < 0.76) return 'rgba(245, 158, 11, 0.28)';
  return 'rgba(239, 68, 68, 0.32)';
};

const nearestPathVector = (point: Point, paths: AirflowPath[]) => {
  let bestDistance = Number.POSITIVE_INFINITY;
  let bestVector = { x: 1, y: 0 };

  for (const path of paths) {
    for (let index = 0; index < path.points.length - 1; index += 1) {
      const from = path.points[index];
      const to = path.points[index + 1];
      const vx = to.x - from.x;
      const vy = to.y - from.y;
      const lengthSq = vx * vx + vy * vy;
      if (lengthSq <= 0) continue;
      const t = Math.max(0, Math.min(1, ((point.x - from.x) * vx + (point.y - from.y) * vy) / lengthSq));
      const projected = { x: from.x + vx * t, y: from.y + vy * t };
      const distance = Math.hypot(point.x - projected.x, point.y - projected.y);
      if (distance < bestDistance) {
        const length = Math.sqrt(lengthSq);
        bestDistance = distance;
        bestVector = { x: vx / length, y: vy / length };
      }
    }
  }

  return { distance: bestDistance, vector: bestVector };
};

const buildPathMetrics = (paths: AirflowPath[]): PathMetric[] => (
  paths.slice(0, MAX_ANIMATED_PATHS).map((path, index) => {
    let length = 0;
    const segments: PathSegment[] = [];

    for (let pointIndex = 0; pointIndex < path.points.length - 1; pointIndex += 1) {
      const from = path.points[pointIndex];
      const to = path.points[pointIndex + 1];
      const dx = to.x - from.x;
      const dy = to.y - from.y;
      const segmentLength = Math.hypot(dx, dy);
      if (segmentLength < 1) continue;
      const vector = { x: dx / segmentLength, y: dy / segmentLength };
      segments.push({
        from,
        to,
        length: segmentLength,
        start: length,
        vector,
        normal: { x: -vector.y, y: vector.x },
      });
      length += segmentLength;
    }

    return { path, index, length, segments };
  }).filter(metric => metric.length > 0)
);

const samplePathMetric = (metric: PathMetric, distance: number) => {
  const wrappedDistance = ((distance % metric.length) + metric.length) % metric.length;
  const segment = metric.segments.find(item => wrappedDistance <= item.start + item.length) ?? metric.segments[metric.segments.length - 1];
  const localDistance = wrappedDistance - segment.start;
  const t = Math.max(0, Math.min(1, localDistance / segment.length));

  return {
    x: segment.from.x + (segment.to.x - segment.from.x) * t,
    y: segment.from.y + (segment.to.y - segment.from.y) * t,
    vector: segment.vector,
    normal: segment.normal,
    progress: wrappedDistance / metric.length,
  };
};

const gradientBands = [
  { offset: 0, radius: 1.8, opacity: 0.78, spacingScale: 1 },
  { offset: 4, radius: 1.35, opacity: 0.48, spacingScale: 1.25 },
  { offset: -4, radius: 1.35, opacity: 0.48, spacingScale: 1.25 },
  { offset: 8, radius: 1.05, opacity: 0.2, spacingScale: 1.8 },
  { offset: -8, radius: 1.05, opacity: 0.2, spacingScale: 1.8 },
];

const buildSourceParticles = (paths: AirflowPath[]): CanvasParticle[] => {
  const source = paths.find(path => path.points.length > 0)?.points[0];
  if (!source) return [];

  return Array.from({ length: SOURCE_PARTICLE_COUNT }, (_, index) => {
    const seed = index * 13.31 + source.x * 0.7 + source.y * 1.3;
    const angle = hash01(seed) * Math.PI * 2;
    const radius = Math.pow(hash01(seed + 2), 1.8) * 32;
    const x = source.x + Math.cos(angle) * radius;
    const y = source.y + Math.sin(angle) * radius;
    const direction = nearestPathVector({ x, y }, paths).vector;
    const intensity = 1 - radius / 34;

    return {
      id: `source-${index}`,
      kind: 'source',
      x,
      y,
      driftX: direction.x * (4 + intensity * 10),
      driftY: direction.y * (4 + intensity * 10),
      offset: 0,
      radius: 1 + intensity * 2.4,
      opacity: 0.18 + intensity * 0.68,
      speed: 0.32 + hash01(seed + 4) * 0.34,
      delay: hash01(seed + 6),
    };
  });
};

const buildCanvasParticles = (paths: AirflowPath[], metrics: PathMetric[]): CanvasParticle[] => {
  const particles: CanvasParticle[] = buildSourceParticles(paths);
  const particleBudget = Math.max(0, MAX_PATH_PARTICLES - particles.length);
  const particlesPerPath = metrics.length > 0 ? Math.ceil(particleBudget / metrics.length) : 0;

  for (const metric of metrics) {
    let pathParticleCount = 0;

    for (const band of gradientBands) {
      const spacing = Math.max(13, Math.min(30, (32 - metric.path.flow * 10) * band.spacingScale));
      const count = Math.min(
        Math.max(4, Math.ceil(metric.length / spacing)),
        Math.max(3, Math.floor(particlesPerPath / gradientBands.length)),
      );
      for (let index = 0; index < count; index += 1) {
        if (particles.length >= MAX_PATH_PARTICLES || pathParticleCount >= particlesPerPath) {
          break;
        }
        const seed = metric.path.flow * 97 + index * 17.7 + band.offset * 19 + particles.length;
        const randomSpread = (hash01(seed) - 0.5) * Math.max(2, Math.abs(band.offset) * 0.62 + 2.6);
        particles.push({
          id: `flow-${metric.path.id}-${band.offset}-${index}`,
          kind: 'path',
          pathIndex: metric.index,
          x: 0,
          y: 0,
          driftX: 0,
          driftY: 0,
          offset: band.offset + randomSpread,
          radius: band.radius + metric.path.flow * 0.42 + hash01(seed + 2) * 0.42,
          opacity: Math.min(0.88, band.opacity + metric.path.opacity * 0.18),
          speed: 34 + metric.path.flow * 44 + hash01(seed + 4) * 30,
          delay: index / count + hash01(seed + 6) * 0.18,
        });
        pathParticleCount += 1;
      }
    }
  }

  return particles;
};

interface AirflowParticleCanvasProps {
  width: number;
  height: number;
  metrics: PathMetric[];
  particles: CanvasParticle[];
}

const AirflowParticleCanvas: React.FC<AirflowParticleCanvasProps> = ({
  width,
  height,
  metrics,
  particles,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const metricsRef = useRef(metrics);
  const particlesRef = useRef(particles);

  useEffect(() => {
    metricsRef.current = metrics;
    particlesRef.current = particles;
  }, [metrics, particles]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || width <= 0 || height <= 0) return undefined;

    const context = canvas.getContext('2d');
    if (!context) return undefined;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    let frameId = 0;
    let disposed = false;

    const resizeCanvas = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const nextWidth = Math.max(1, Math.floor(rect.width * dpr));
      const nextHeight = Math.max(1, Math.floor(rect.height * dpr));
      if (canvas.width !== nextWidth || canvas.height !== nextHeight) {
        canvas.width = nextWidth;
        canvas.height = nextHeight;
      }
    };

    const drawDot = (
      x: number,
      y: number,
      radius: number,
      opacity: number,
      scaleX: number,
      scaleY: number,
    ) => {
      const px = x * scaleX;
      const py = y * scaleY;
      const scaledRadius = Math.max(0.75, radius * Math.min(scaleX, scaleY));
      context.globalAlpha = opacity;
      context.beginPath();
      context.arc(px, py, scaledRadius, 0, Math.PI * 2);
      context.fill();
    };

    const render = (time: number) => {
      if (disposed) return;
      resizeCanvas();

      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / width;
      const scaleY = canvas.height / height;
      context.clearRect(0, 0, canvas.width, canvas.height);

      if (!mediaQuery.matches && rect.width > 0 && rect.height > 0) {
        context.fillStyle = '#1d8cff';
        context.shadowColor = 'rgba(29, 140, 255, 0.42)';
        context.shadowBlur = 8 * Math.min(scaleX, scaleY);

        for (const particle of particlesRef.current) {
          if (particle.kind === 'source') {
            const phase = (time / 1000 * particle.speed + particle.delay) % 1;
            const wave = Math.sin(phase * Math.PI * 2);
            drawDot(
              particle.x + particle.driftX * phase,
              particle.y + particle.driftY * phase,
              particle.radius * (0.86 + Math.abs(wave) * 0.22),
              particle.opacity * (0.45 + (1 - phase) * 0.55),
              scaleX,
              scaleY,
            );
            continue;
          }

          const metric = metricsRef.current.find(item => item.index === particle.pathIndex);
          if (!metric) continue;
          const distance = (time / 1000 * particle.speed + particle.delay * metric.length) % metric.length;
          const sample = samplePathMetric(metric, distance);
          const edgeFade = Math.min(1, sample.progress / 0.12, (1 - sample.progress) / 0.12);
          drawDot(
            sample.x + sample.normal.x * particle.offset,
            sample.y + sample.normal.y * particle.offset,
            particle.radius,
            particle.opacity * Math.max(0, edgeFade),
            scaleX,
            scaleY,
          );
        }
      }

      frameId = window.requestAnimationFrame(render);
    };

    frameId = window.requestAnimationFrame(render);
    return () => {
      disposed = true;
      window.cancelAnimationFrame(frameId);
    };
  }, [height, width]);

  return (
    <canvas
      ref={canvasRef}
      className="airflow-particle-canvas"
      aria-hidden="true"
    />
  );
};

const AirflowVisualization: React.FC<AirflowVisualizationProps> = ({
  imageAnalysis,
  airflowAnalysis,
  previewUrl,
}) => {
  const planImageUrl =
    imageAnalysis.yoloAnalysis?.airflowVisualizationImage ??
    imageAnalysis.yoloAnalysis?.visualizationImage ??
    previewUrl;
  const nodeLabels = useMemo(() => {
    const labels = new Map<string, string>();
    airflowAnalysis.graph.nodes.forEach((node) => labels.set(node.id, node.label));
    return labels;
  }, [airflowAnalysis.graph.nodes]);
  const mainPath = airflowAnalysis.paths[0];
  const sortedHeatmap = useMemo(
    () => [...airflowAnalysis.visualization.heatmap].sort((a, b) => b.flow - a.flow),
    [airflowAnalysis.visualization.heatmap],
  );
  const pathMetrics = useMemo(
    () => buildPathMetrics(airflowAnalysis.paths),
    [airflowAnalysis.paths],
  );
  const canvasParticles = useMemo(
    () => buildCanvasParticles(airflowAnalysis.paths, pathMetrics),
    [airflowAnalysis.paths, pathMetrics],
  );

  const renderPathLabel = (path: AirflowPath) => (
    path.nodes.map((nodeId) => nodeLabels.get(nodeId) ?? nodeId).join(' → ')
  );

  return (
    <Card className="result-card airflow-card" title="气流路径 Graph + Flow" bordered={false}>
      <div className="airflow-module">
        <div className="airflow-copy">
          <span className="section-kicker">Airflow topology</span>
          <Title level={3}>入户气口到各空间的流动路径</Title>
          <Paragraph>{airflowAnalysis.summary}</Paragraph>
        </div>

        <Row gutter={[22, 22]}>
          <Col xs={24} lg={14}>
            <div className="airflow-stage">
              <svg
                className="airflow-svg"
                viewBox={`0 0 ${airflowAnalysis.visualization.width} ${airflowAnalysis.visualization.height}`}
                role="img"
                aria-label="气流路径可视化"
              >
                <image
                  href={planImageUrl}
                  x="0"
                  y="0"
                  width={airflowAnalysis.visualization.width}
                  height={airflowAnalysis.visualization.height}
                  preserveAspectRatio="xMidYMid meet"
                  opacity="0.72"
                />

                {airflowAnalysis.visualization.gridField ? (
                  <g className="airflow-cloud-layer">
                    {airflowAnalysis.visualization.gridField.velocityMag.flatMap((row, y) => (
                      row.map((value, x) => {
                        const field = airflowAnalysis.visualization.gridField!;
                        if (field.solid[y]?.[x]) return null;
                        const opacity = Math.max(0.08, Math.min(0.48, 0.12 + value * 0.34));
                        return (
                          <rect
                            key={`cloud-${x}-${y}`}
                            x={field.origin.x + x * field.dx}
                            y={field.origin.y + y * field.dy}
                            width={field.dx + 0.5}
                            height={field.dy + 0.5}
                            fill={airflowCloudColor(value)}
                            opacity={opacity}
                          />
                        );
                      })
                    ))}
                  </g>
                ) : null}

                {airflowAnalysis.visualization.heatmap.map((cell) => {
                  const [x1, y1, x2, y2] = cell.bbox;
                  return (
                    <g key={cell.roomId}>
                      <rect
                        x={x1}
                        y={y1}
                        width={x2 - x1}
                        height={y2 - y1}
                        fill={cell.color}
                        opacity={cell.opacity}
                        stroke={cell.color}
                        strokeWidth="2"
                      />
                      <text
                        x={cell.center.x}
                        y={cell.center.y}
                        className="airflow-room-label"
                        textAnchor="middle"
                        dominantBaseline="middle"
                      >
                        {cell.label} {formatPercent(cell.flow)}
                      </text>
                    </g>
                  );
                })}

                <g className="airflow-path-layer">
                  {airflowAnalysis.paths.map((path) => (
                    <g key={path.id}>
                      <path
                        d={path.svgPath}
                        className="airflow-path-glow"
                        strokeWidth={path.strokeWidth + 10}
                        opacity={path.opacity * 0.24}
                      />
                      <path
                        d={path.svgPath}
                        className="airflow-path-core"
                        strokeWidth={path.strokeWidth}
                        opacity={path.opacity}
                      />
                    </g>
                  ))}
                </g>

                {airflowAnalysis.visualization.sourcePoints.map((point, index) => (
                  <g key={`${point.x}-${point.y}-${index}`} className="airflow-source">
                    <circle cx={point.x} cy={point.y} r="13" />
                    <circle cx={point.x} cy={point.y} r="26" />
                  </g>
                ))}
              </svg>
              <AirflowParticleCanvas
                width={airflowAnalysis.visualization.width}
                height={airflowAnalysis.visualization.height}
                metrics={pathMetrics}
                particles={canvasParticles}
              />
            </div>
          </Col>

          <Col xs={24} lg={10}>
            <div className="airflow-side">
              <div className="airflow-stat-grid">
                <div>
                  <BranchesOutlined />
                  <strong>{airflowAnalysis.graph.nodes.length}</strong>
                  <span>节点</span>
                </div>
                <div>
                  <ApartmentOutlined />
                  <strong>{airflowAnalysis.graph.edges.length}</strong>
                  <span>连线</span>
                </div>
                <div>
                  <ThunderboltOutlined />
                  <strong>{airflowAnalysis.paths.length}</strong>
                  <span>路径</span>
                </div>
                <div>
                  <FireOutlined />
                  <strong>{airflowAnalysis.visualization.gridField ? formatPercent(airflowAnalysis.visualization.gridField.metrics.straightThroughRisk) : 0}</strong>
                  <span>穿堂风险</span>
                </div>
              </div>

              {airflowAnalysis.visualization.gridField && (
                <div className="airflow-main-path">
                  <span>栅格流场</span>
                  <strong>
                    死气区 {formatPercent(airflowAnalysis.visualization.gridField.metrics.deadZoneRatio)}%
                    {' · '}
                    高速区 {formatPercent(airflowAnalysis.visualization.gridField.metrics.highSpeedRatio)}%
                    {' · '}
                    通风分 {formatPercent(airflowAnalysis.visualization.gridField.metrics.ventilationScore)}
                  </strong>
                  <Progress
                    percent={formatPercent(airflowAnalysis.visualization.gridField.metrics.ventilationScore)}
                    showInfo={false}
                    strokeColor="#1d8cff"
                  />
                </div>
              )}

              {mainPath && (
                <div className="airflow-main-path">
                  <span>主气流路径</span>
                  <strong>{renderPathLabel(mainPath)}</strong>
                  <Progress percent={formatPercent(mainPath.flow)} showInfo={false} strokeColor="#8f3f2a" />
                </div>
              )}

              <div className="airflow-room-list">
                {sortedHeatmap.map((cell) => (
                  <div className="airflow-room-row" key={cell.roomId}>
                    <div>
                      <strong>{cell.label}</strong>
                      <Tag color={levelTagColors[cell.level]}>{levelLabels[cell.level]}</Tag>
                    </div>
                    <Progress
                      percent={formatPercent(cell.flow)}
                      size="small"
                      showInfo={false}
                      strokeColor={cell.color}
                    />
                  </div>
                ))}
              </div>

              {airflowAnalysis.findings.length > 0 && (
                <div className="airflow-findings">
                  {airflowAnalysis.findings.slice(0, 3).map((finding) => (
                    <div key={`${finding.roomId}-${finding.message}`}>
                      <FireOutlined />
                      <p>
                        <strong>{finding.message}</strong>
                        <span>{finding.suggestion}</span>
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Col>
        </Row>
      </div>
    </Card>
  );
};

export default AirflowVisualization;
